import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEvents } from '../hooks/useEvents';
import EventDetailModal from './EventDetailModal';
import '../styles/EventsCalendar.css';

export default function EventsCalendar({ region, onClose, defaultTab }) {
    const { t } = useTranslation();
    const { events, loading, loadEvents: refreshEvents } = useEvents(region);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [activeTab, setActiveTab] = useState('trail_buddy');

    // Effetto per aggiornare il tab quando viene passato dall'esterno
    useEffect(() => {
        if (defaultTab) {
            setActiveTab(defaultTab);
        }
    }, [defaultTab]);

    // Filtra eventi per tipo
    const trailBuddyEvents = events.filter(e => e.eventType === 'trail_buddy');
    const activityEvents = events.filter(e => e.eventType === 'activity');
    const currentEvents = activeTab === 'trail_buddy' ? trailBuddyEvents : activityEvents;

    const eventsByMonth = currentEvents.reduce((acc, event) => {
        const date = new Date(event.eventDate);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!acc[monthKey]) acc[monthKey] = [];
        acc[monthKey].push(event);
        return acc;
    }, {});

    const months = Object.keys(eventsByMonth).sort();

    const formatMonthHeader = (monthKey) => {
        const [year, month] = monthKey.split('-');
        const date = new Date(year, parseInt(month) - 1);
        return date.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
    };

    if (loading) {
        return (
            <div className="events-calendar-overlay">
                <div className="events-calendar-modal">
                    <div className="loading-spinner">⏳ {t('loading')}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="events-calendar-overlay" onClick={onClose}>
            <div className="events-calendar-modal" onClick={(e) => e.stopPropagation()}>
                <div className="calendar-header">
                    <h2>🥾👥 {t('find_buddies')}</h2>
                    <button className="close-button" onClick={onClose}>✕</button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0 8px 0' }}>
                    <span style={{ fontWeight: 600, fontSize: '18px', marginLeft: '18px' }}>{t('buddies_found')}:</span>
                    <span className="tab-count" style={{ fontSize: '18px', background: '#e8f5e9', color: '#388e3c', padding: '4px 14px', borderRadius: '16px', fontWeight: 700 }}>{trailBuddyEvents.length}</span>
                </div>

                <div className="calendar-stats">
                    <div className="stat-item">
                        <span className="stat-value">{trailBuddyEvents.length}</span>
                        <span className="stat-label">{t('buddies_wanted')}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{months.length}</span>
                        <span className="stat-label">{t('active_months')}</span>
                    </div>
                </div>

                {trailBuddyEvents.length === 0 ? (
                    <div className="no-events">
                        <p>😕 {t('no_buddy_requests')}</p>
                        <p>{t('be_first_buddy')}</p>
                    </div>
                ) : (
                    <div className="calendar-content">
                        {months.map(monthKey => (
                            <div key={monthKey} className="month-section">
                                <h3 className="month-header">{formatMonthHeader(monthKey)}</h3>
                                <div className="events-list">
                                    {eventsByMonth[monthKey].map(event => (
                                        <div
                                            key={event._id}
                                            className={`event-card ${event.eventType}`}
                                            onClick={() => setSelectedEvent(event)}
                                        >
                                            <div className="event-date">
                                                <div className="date-day">
                                                    {new Date(event.eventDate).getDate()}
                                                </div>
                                                <div className="date-month">
                                                    {new Date(event.eventDate).toLocaleDateString('it-IT', { month: 'short' })}
                                                </div>
                                            </div>
                                            <div className="event-info">
                                                <h4>
                                                    🥾👥 {event.eventName}
                                                </h4>
                                                <div className="event-trail">
                                                    📍 {event.trail?.name || event.poi?.name || 'Location'}
                                                </div>
                                                <div className="event-meta">
                                                    {event.meetingTime && <span>⏰ {event.meetingTime}</span>}
                                                    {event.spotsLeft != null && (
                                                        <span className={`spots-badge ${event.spotsLeft <= 0 ? 'full' : ''}`}>
                                                            {event.spotsLeft > 0
                                                                ? `👥 ${event.spotsLeft} ${event.spotsLeft === 1 ? t('spot_singular') : t('spot_plural')}`
                                                                : `🔴 ${t('full')}`
                                                            }
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="event-arrow">›</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {selectedEvent && (
                    <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
                )}
            </div>
        </div>
    );
}
